import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { CreatePlanDialogComponent } from '../../Dialogs/create-plan-dialog/create-plan-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvPaginatorComponent } from '../../../../Shared/Components/av-paginator/av-paginator.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { PlansFacade } from '../../../../../Facades/Plans/plans.facade';
import { DATE_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  FEATURE_AGENT_KEY,
  PLAN_STATUS_LABEL,
  PLAN_STATUS_OPTIONS,
} from '../../../../../Consts/plans.const';
import { PlanSummary } from '../../../../../Models/backoffice/plan-summary.model';
import { CreatePlanDialogResult } from '../../../Interfaces/plan-dialog.interface';

@Component({
  selector: 'app-plans',
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvPaginatorComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './plans.component.html',
  styleUrl: './plans.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class PlansComponent {
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(PlansFacade);

  readonly dateFormat = DATE_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly statusOptions = PLAN_STATUS_OPTIONS;
  readonly statusLabelKeys = PLAN_STATUS_LABEL;
  readonly agentKey = FEATURE_AGENT_KEY;

  readonly emptyTitleKey = computed<string>(() =>
    this.facade.hasActiveFilters()
      ? 'backoffice.plans.list.filtered_empty.title'
      : 'backoffice.plans.list.empty.title',
  );

  readonly emptyMessageKey = computed<string>(() =>
    this.facade.hasActiveFilters()
      ? 'backoffice.plans.list.filtered_empty.message'
      : 'backoffice.plans.list.empty.message',
  );

  search(value: string): void {
    this.facade.applyFilters({ ...this.facade.filters(), search: value });
  }

  filterStatus(value: string): void {
    this.facade.applyFilters({ ...this.facade.filters(), status: value });
  }

  filterMarket(value: string): void {
    this.facade.applyFilters({ ...this.facade.filters(), market_id: value });
  }

  clearFilters(): void {
    this.facade.clearFilters();
  }

  goToPage(page: number): void {
    this.facade.goToPage(page);
  }

  changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  openPlan(plan: PlanSummary): void {
    this._router.navigate(['/plans', plan.id]);
  }

  openCreate(): void {
    const reference = this._dialog.open(CreatePlanDialogComponent, {
      data: { markets: this.facade.markets() },
    });

    reference.afterClosed().subscribe((result?: CreatePlanDialogResult) => this._applyCreate(result));
  }

  hasAgent(plan: PlanSummary): boolean {
    return plan.feature_keys.includes(this.agentKey);
  }

  private _applyCreate(result?: CreatePlanDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.create({
      market_id: result.marketId,
      code: result.code,
      name: result.name,
      version: result.version,
      is_active: result.isActive,
    });
  }
}
